import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import { CNPJ_MASK, CPF_MASK } from '@shared/constants';
import { PasswordService } from '@shared/services/password.service';
import { AlertService } from '@shared/services/alert.service';
import { UserService } from '@shared/services/user.service';
import { AuthService } from '@shared/services/auth.service';
import { TenantService } from '@core/tenant/tenant.service';
import { UserProfile } from '@shared/models';
import { CameraCaptureDialogComponent } from '@core/layout/header/user-profile-modal/camera-capture-dialog/camera-capture-dialog.component';
import { ZDialogComponent } from '../../../../shared/components/z-dialog/z-dialog.component';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ZButtonComponent } from '../../../../shared/components/z-button/z-button.component';
import { AsyncPipe } from '@angular/common';
import { NgxMaskPipe } from 'ngx-mask';

interface ProfileViewModel {
    fullName: string;
    company: string;
    cpf: string;
    email: string;
    registrationDate: string;
    group: string;
    profileImageUrl: string | null;
}

@Component({
    selector: 'app-user-profile-modal',
    templateUrl: './user-profile-modal.component.html',
    styleUrls: ['./user-profile-modal.component.scss'],
    imports: [ZDialogComponent, MatIcon, MatProgressSpinner, MatMenuTrigger, MatMenu, MatMenuItem, ZButtonComponent, AsyncPipe, TranslatePipe, NgxMaskPipe]
})
export class UserProfileModalComponent implements OnInit {

    readonly cpfMask = CPF_MASK;
    readonly cnpjMask = CNPJ_MASK;

    private readonly passwordService = inject(PasswordService);
    private readonly alertService = inject(AlertService);
    private readonly translate = inject(TranslateService);
    private readonly userService = inject(UserService);
    private readonly authService = inject(AuthService);
    private readonly tenantService = inject(TenantService);
    private readonly dialog = inject(MatDialog);

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    readonly changePasswordLoading = new BehaviorSubject<boolean>(false);
    readonly imageLoading = signal<boolean>(false);
    readonly profileLoading = signal<boolean>(true);

    readonly user = signal<ProfileViewModel>(this.buildInitialProfile());

    constructor(public dialogRef: MatDialogRef<UserProfileModalComponent>) {}

    private buildInitialProfile(): ProfileViewModel {
        const session = this.authService.currentUser();
        return {
            fullName: session?.name ?? '',
            company: this.tenantService.tenantName(),
            cpf: '',
            email: session?.email ?? '',
            registrationDate: '',
            group: '',
            profileImageUrl: session?.profileImageUrl ?? null,
        };
    }

    ngOnInit(): void {
        this.loadProfile();
    }

    private loadProfile(): void {
        const userId = this.authService.currentUser()?.id;
        if (!userId) {
            this.profileLoading.set(false);
            return;
        }

        this.userService.getById(userId).subscribe({
            next: (response) => {
                const profile = response.data;
                if (!profile) {
                    this.profileLoading.set(false);
                    return;
                }
                this.user.set(this.mapProfile(profile));
                this.authService.updateProfileImageUrl(profile.profileImagePresignedUrl ?? null);
                this.profileLoading.set(false);
            },
            error: () => {
                this.profileLoading.set(false);
                this.alertService.showErrorMessage(
                    this.translate.instant('HEADER.USER_MENU.PROFILE_LOAD_ERROR'),
                );
            },
        });
    }

    private mapProfile(profile: UserProfile): ProfileViewModel {
        const fullName = `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim()
            || profile.username
            || '';
        const cpf = profile.attributes?.['documentNumber']?.[0] ?? '';
        const registrationDate = profile.attributes?.['registrationDate']?.[0]
            ?? profile.attributes?.['createdAt']?.[0]
            ?? '';
        const group = profile.groups?.map((g) => g.name).filter(Boolean).join(', ') ?? '';

        return {
            fullName,
            company: this.tenantService.tenantName(),
            cpf,
            email: profile.email ?? '',
            registrationDate,
            group,
            profileImageUrl: profile.profileImagePresignedUrl ?? null,
        };
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onChangePassword(): void {
        const email = this.user().email;
        if (!email || this.changePasswordLoading.value) return;

        this.changePasswordLoading.next(true);
        this.passwordService.requestReset({ email }).subscribe({
            next: () => {
                this.changePasswordLoading.next(false);
                this.alertService.showSuccessMessage(
                    this.translate.instant('AUTH.FORGOT_PASSWORD.SUCCESS_MESSAGE'),
                );
                this.dialogRef.close();
            },
            error: () => {
                this.changePasswordLoading.next(false);
                this.alertService.showErrorMessage(
                    this.translate.instant('AUTH.FORGOT_PASSWORD.ERROR_MESSAGE'),
                );
            },
        });
    }

    triggerFileInput(): void {
        if (this.imageLoading()) return;
        this.fileInput.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            this.alertService.showErrorMessage(
                this.translate.instant('HEADER.USER_MENU.PROFILE_IMAGE_INVALID_TYPE'),
            );
            return;
        }

        this.uploadImage(file);
    }

    openCameraDialog(): void {
        if (this.imageLoading()) return;

        const ref = this.dialog.open(CameraCaptureDialogComponent, {
            width: '640px',
            maxWidth: '95vw',
            panelClass: 'custom-profile-modal',
        });

        ref.afterClosed().subscribe((file: File | null | undefined) => {
            if (file) this.uploadImage(file);
        });
    }

    private uploadImage(file: File): void {
        const hasImage = !!this.user().profileImageUrl;
        const request$ = hasImage
            ? this.userService.updateProfileImage(file)
            : this.userService.uploadProfileImage(file);

        this.imageLoading.set(true);
        request$.subscribe({
            next: () => this.refreshAfterImageChange(true),
            error: () => {
                this.imageLoading.set(false);
                this.alertService.showErrorMessage(
                    this.translate.instant('HEADER.USER_MENU.PROFILE_IMAGE_UPLOAD_ERROR'),
                );
            },
        });
    }

    onDeleteImage(): void {
        if (this.imageLoading() || !this.user().profileImageUrl) return;

        this.imageLoading.set(true);
        this.userService.deleteProfileImage().subscribe({
            next: () => this.refreshAfterImageChange(false),
            error: () => {
                this.imageLoading.set(false);
                this.alertService.showErrorMessage(
                    this.translate.instant('HEADER.USER_MENU.PROFILE_IMAGE_DELETE_ERROR'),
                );
            },
        });
    }

    private refreshAfterImageChange(uploaded: boolean): void {
        const userId = this.authService.currentUser()?.id;
        if (!userId) {
            this.imageLoading.set(false);
            return;
        }

        this.userService.getById(userId).subscribe({
            next: (response) => {
                const url = response.data?.profileImagePresignedUrl ?? null;
                this.user.update((u) => ({ ...u, profileImageUrl: url }));
                this.authService.updateProfileImageUrl(url);
                this.imageLoading.set(false);
                const messageKey = uploaded
                    ? 'HEADER.USER_MENU.PROFILE_IMAGE_UPLOAD_SUCCESS'
                    : 'HEADER.USER_MENU.PROFILE_IMAGE_DELETE_SUCCESS';
                this.alertService.showSuccessMessage(this.translate.instant(messageKey));
            },
            error: () => {
                this.imageLoading.set(false);
            },
        });
    }
}
